<template>
  <div class="relative">
    <div v-show="isLeftBtnVisible" class="absolute -left-6 top-0 hidden w-4 cursor-pointer items-center justify-center xl:flex" :class="`${props.scrollButtonBoxStyle}`" @click="scrollLeft">
      <img alt="" class="h-6 w-3" src="~@/assets/img/parts/top-left.svg" />
    </div>
    <div ref="scrollBox" class="flex w-full overflow-x-scroll" :class="props.scrollBoxStyle">
      <div class="flex" :class="[`gap-${props.gap}`, props.isSingleChildFilled ? 'min-w-full' : '']">
        <slot></slot>
      </div>
    </div>
    <div v-show="isRightBtnVisible" class="absolute -right-6 top-0 hidden w-4 cursor-pointer items-center justify-center xl:flex" :class="`${props.scrollButtonBoxStyle}`" @click="scrollRight">
      <img alt="" class="h-6 w-3" src="~@/assets/img/parts/top-right.svg" />
    </div>
  </div>
</template>

<script lang="ts" setup>
interface Props {
  scrollButtonBoxStyle?: string;
  scrollBoxStyle?: string;
  refresh?: boolean;
  gap?: number;
  /**
   * スライドが1枚のとき、親要素幅100%で埋めるか？
   */
  isSingleChildFilled?: boolean;
  /**
   * 端の誤差許容（px）。左右端判定と幅比較に使う
   */
  edgeAllowancePx?: number;
}

const props = withDefaults(defineProps<Props>(), {
  scrollButtonBoxStyle: '',
  scrollBoxStyle: '',
  refresh: false,
  gap: 2,
  isSingleChildFilled: false,
  edgeAllowancePx: 1,
});

const scrollBox = ref<HTMLElement>();
const isLeftBtnVisible = ref<boolean>(false);
const isRightBtnVisible = ref<boolean>(false);

const scroll = (position: number) => {
  if (scrollBox.value == null) return;

  scrollBox.value.scrollTo({
    left: position,
    behavior: 'smooth',
  });
};

const setBtnVisibility = () => {
  if (scrollBox.value == null) return;
  const scrollWidth = scrollBox.value.scrollWidth;
  const clientWidth = scrollBox.value.clientWidth;
  const scrollLeft = scrollBox.value.scrollLeft;

  // スクロール幅がクライアント幅と同等（誤差含む）の場合はボタンを表示しない
  if (scrollWidth - clientWidth <= props.edgeAllowancePx) {
    isLeftBtnVisible.value = false;
    isRightBtnVisible.value = false;
    return;
  }

  // 左端にいる場合は右側だけボタン表示
  if (scrollLeft <= props.edgeAllowancePx) {
    isLeftBtnVisible.value = false;
    isRightBtnVisible.value = true;
    return;
  }

  // 次の右側へのスクロールではみ出す場合は左側だけボタン表示
  if (scrollLeft + clientWidth >= scrollWidth - props.edgeAllowancePx) {
    isLeftBtnVisible.value = true;
    isRightBtnVisible.value = false;
    return;
  }

  // それ以外は両側ともボタン表示
  isLeftBtnVisible.value = true;
  isRightBtnVisible.value = true;
};

const scrollLeft = () => {
  if (scrollBox.value == null) return;

  const scrollTo = Math.max(0, scrollBox.value.scrollLeft - scrollBox.value.clientWidth);
  scroll(scrollTo);
  setBtnVisibility();
};

const scrollRight = () => {
  if (scrollBox.value == null) return;

  const scrollTo = Math.min(scrollBox.value.scrollWidth - scrollBox.value.clientWidth, scrollBox.value.scrollLeft + scrollBox.value.clientWidth);
  scroll(scrollTo);
  setBtnVisibility();
};

onMounted(() => {
  setBtnVisibility();
  scrollBox.value?.addEventListener('scroll', () => {
    setBtnVisibility();
  });
  // 画面サイズ変更時にも再計算
  window.addEventListener('resize', setBtnVisibility);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', setBtnVisibility);
});

watch(
  () => props.refresh,
  (refresh) => {
    if (refresh) setBtnVisibility();
  },
);
</script>

<style lang="scss" scoped>
::-webkit-scrollbar {
  height: 1px;
}

::-webkit-scrollbar-track {
  background: #fff;
  @media only screen and (min-width: 768px) {
    background: transparent;
  }
}
::-webkit-scrollbar-thumb {
  background: #836324;
  @media only screen and (min-width: 768px) {
    background: transparent;
  }
}
</style>
